import { FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { siteMasterPaths } from '@/lib/siteMasterPaths';
import { APP_BRAND_TITLE } from '@/lib/appVersion';

const inputClass =
  'w-full rounded-2xl border-2 border-slate-200 bg-white px-5 py-4 text-xl text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-4 focus:ring-teal-500/20';

export function SiteMasterLoginPage() {
  const { ok, loading } = useSiteMasterSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('site_master_login', {
        method: 'POST',
        body: JSON.stringify({
          phone: phone.replace(/\s/g, ''),
          password,
        }),
      });
      if (r.ok) {
        const next = searchParams.get('next');
        const target =
          next && next.startsWith('/w/') && !next.startsWith('/w/login') ? next : siteMasterPaths.home;
        navigate(target, { replace: true });
      } else {
        setErr(r.detail ?? (r.err === 'bad_credentials' ? '手机号或密码不正确' : '登录失败'));
      }
    } catch {
      setErr('网络异常，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  }

  if (!loading && ok) {
    const next = searchParams.get('next');
    const target =
      next && next.startsWith('/w/') && !next.startsWith('/w/login') ? next : siteMasterPaths.home;
    return <Navigate to={target} replace />;
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-gradient-to-b from-teal-700 to-teal-900"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
    >
      <header className="px-5 pt-4 text-center text-white">
        <p className="text-2xl font-bold tracking-tight">{APP_BRAND_TITLE}</p>
        <p className="mt-2 text-lg text-teal-100">站长工作台</p>
      </header>

      <main className="mx-auto mt-8 w-full max-w-md flex-1 px-4">
        <div className="rounded-3xl bg-white px-6 py-8 shadow-2xl">
          <h1 className="text-center text-2xl font-bold text-slate-900">登录</h1>
          <p className="mt-2 text-center text-base leading-relaxed text-slate-600">
            请使用管理员提供的手机号登录
            <br />
            首次登录默认密码为 <strong className="text-slate-800">88888888</strong>
          </p>

          {err ? (
            <p className="mt-5 rounded-2xl bg-red-50 px-4 py-3 text-center text-lg text-red-800" role="alert">
              {err}
            </p>
          ) : null}

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <label className="mb-2 block text-lg font-semibold text-slate-700" htmlFor="sm-phone">
                手机号
              </label>
              <input
                id="sm-phone"
                className={inputClass}
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="11 位手机号"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="mb-2 block text-lg font-semibold text-slate-700" htmlFor="sm-pass">
                密码
              </label>
              <input
                id="sm-pass"
                className={inputClass}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-2 w-full rounded-2xl bg-teal-600 py-4 text-xl font-bold text-white shadow-lg active:bg-teal-700 disabled:opacity-60"
            >
              {submitting ? '登录中…' : '进入管理'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
